// ExportPromotionUsed.ts
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { toBuddhistYear } from 'utils/app.utils'
import { forceTextOnDateCells } from 'utils/excel.utils'

export interface UsedRow {
    date: string   // “YYYY-MM-DD”
    receiptNumber: string
    cnNumber: string
    branchCnNumber: string
    snCode?: string
    patientName: string
    promotionType: string
    promotionName: string
    format: string   // “EXTRA” | “DISCOUNT”
    promotionPrice: string
    promotionDiscount: number
}

export interface GroupedPromo {
    promotionType: string
    items: Array<{
        promotionName: string
        value: string
        countUsed: number
        sumDiscount: number
    }>
}

export interface PromotionUsedReport {
    branchName: string
    dateStart: string   // “YYYY-MM-DD”
    dateEnd: string
    typeFilter: string   // e.g. “ทั้งหมด”
    promoFilter: string
    typePromotion: string
    includeSn: boolean
    rows: UsedRow[]
    groups: GroupedPromo[]
    t: (key: string) => string
}

export async function exportPromotionUsedToExcel(rpt: PromotionUsedReport) {
    const wb = new ExcelJS.Workbook()
    const ws: any = wb.addWorksheet(rpt.t('REPORT.TABLE.PROMOTION_USED.TITLE'), {
        pageSetup: { orientation: 'landscape' }
    })
    ws.properties.defaultRowHeight = 20

    // 1) กำหนดคอลัมน์ A–L
    const cols: Partial<ExcelJS.Column>[] = [
        { header: 'ลำดับ', width: 6 },
        { header: 'วันที่', width: 12 },
        { header: 'เลขที่ใบเสร็จ', width: 20 },
        { header: 'รหัสผู้ป่วย', width: 14 },
        { header: 'รหัสผู้ป่วยในสาขา', width: 18 },
        ...(rpt.includeSn ? [{ header: 'SN', width: 12 }] : []),
        { header: 'ชื่อผู้ป่วย', width: 20 },
        { header: 'ประเภทโปรโมชัน', width: 18 },
        { header: 'โปรโมชัน', width: 30 },
        { header: 'รูปแบบโปรโมชัน', width: 12 },
        { header: 'มูลค่า', width: 12 },
        { header: 'ยอดลด (บาท)', width: 14 }
    ]
    ws.columns = cols as ExcelJS.Column[]

    let row = 1

    // 2) Title
    ws.mergeCells(row, 1, row, ws.columns.length)
    ws.getCell(row, 1).value = rpt.t('REPORT.TABLE.PROMOTION_USED.TITLE')
    ws.getCell(row, 1).font = { size: 16, bold: true }
    ws.getCell(row, 1).alignment = { horizontal: 'center' }
    row++

    // 3) Filters
    ws.mergeCells(row, 1, row, ws.columns.length)
    ws.getCell(row, 1).value = [
        `${rpt.t('REPORT.FILTER.BRANCH')}: ${rpt.branchName}`,
        `${rpt.t('REPORT.FILTER.START')}:  ${rpt.dateStart}`,
        `${rpt.t('REPORT.FILTER.TO')}:   ${rpt.dateEnd}`,
        `${rpt.t('REPORT.FILTER.PROMOTION_USED.TYPE')}: ${rpt.typeFilter}`,
        `${rpt.t('REPORT.FILTER.PROMOTION_USED.PROMOTION')}: ${rpt.promoFilter}`,
        `${rpt.t('REPORT.FILTER.PROMOTION_USED.FORMAT')}: ${rpt.typePromotion}`
    ].join('    ')
    ws.getCell(row, 1).font = { size: 12 }
    ws.getCell(row, 1).alignment = { horizontal: 'left' }
    row += 2

    // 4) Header row
    const headerRow = ws.addRow(ws.columns.map((c: any) => (c.header as string)))
    headerRow.height = 24
    headerRow.font = { bold: true }
    headerRow.alignment = { horizontal: 'center', vertical: 'middle' }
    headerRow.eachCell((cell: any) => {
        cell.border = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
        }
    })
    row++

    if (!rpt.rows || rpt.rows.length === 0) {
        const noDataRow = ws.addRow([rpt.t('REPORT.TITLE.NO_DATA')])
        ws.mergeCells(noDataRow.number, 1, noDataRow.number, cols.length)
        noDataRow.font = { italic: true, color: { argb: 'FF000000' } }
        noDataRow.alignment = { horizontal: 'center' }
    }

    // 5) Data rows
    rpt.rows.forEach((d: any, i: number) => {
        const vals: any[] = [
            i + 1,
            d.date ? toBuddhistYear(moment(d.date), 'DD/MM/YYYY') : '-',
            d.receiptNumber,
            d.cnNumber,
            d.branchCnNumber
        ]
        if (rpt.includeSn) vals.push(d.snCode || '-')
        vals.push(
            d.patientName,
            d.promotionTypeName,
            d.promotionName,
            d.type,
            d.promotionPrice,
            d.promotionDiscount
        )
        const r = ws.addRow(vals)
        r.alignment = { vertical: 'middle' }
        r.eachCell((cell: any, col: any) => {
            // ขอบ cell
            cell.border = {
                top: { style: 'thin' }, left: { style: 'thin' },
                bottom: { style: 'thin' }, right: { style: 'thin' }
            }

            // คอลัมน์แรก (i+1) จัดกลางแนวนอน
            if (col === 1) {
                cell.alignment = { horizontal: 'center', vertical: 'middle' }
            }
            // คอลัมน์ “ยอดลด (บาท)” จัดขวา
            else if (col === ws.columns.length) {
                cell.alignment = { horizontal: 'right', vertical: 'middle' }
                if (typeof cell.value === 'number') cell.numFmt = '#,##0.00'
            }
            // คอลัมน์มูลค่า จัดขวา
            else if (col === ws.columns.length - 1) {
                cell.alignment = { horizontal: 'right', vertical: 'middle' }
            }
        })
    })

    // 6) ว่าง 1 แถว
    row = ws.lastRow.number + 1
    ws.addRow([])
    // 7) Summary header
    const sumCols = [
        { header: 'ประเภทโปรโมชัน', width: 18 },
        { header: 'โปรโมชัน', width: 30 },
        { header: 'มูลค่า', width: 12 },
        { header: 'จำนวน (ครั้ง)', width: 12 },
        { header: 'ยอดลด (บาท)', width: 14 }
    ]
    // override widths
    sumCols.forEach((c, i) => ws.getColumn(i + 1).width = c.width)
    const sumHeader = ws.addRow(sumCols.map(c => c.header))
    sumHeader.font = { bold: true }
    sumHeader.alignment = { horizontal: 'center', vertical: 'middle' }
    sumHeader.eachCell((cell: any) => {
        cell.border = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
        }
    })
    row++

    if (!rpt.groups || rpt.groups.length === 0) {
        const noDataRow = ws.addRow([rpt.t('REPORT.TITLE.NO_DATA')])
        ws.mergeCells(noDataRow.number, 1, noDataRow.number, sumCols.length)
        noDataRow.font = { italic: true, color: { argb: 'FF000000' } }
        noDataRow.alignment = { horizontal: 'center' }
        forceTextOnDateCells(wb)
        const buf = await wb.xlsx.writeBuffer()
        saveAs(
            new Blob([buf]),
            `${rpt.t('REPORT.TABLE.PROMOTION_USED.TITLE')}_${moment().format('YYYYMMDD')}.xlsx`
        )
        return
    }

    // 8) Summary data groups
    rpt.groups.forEach((g: any) => {
        const start = row
        g.promotions.forEach((v: any, i: number) => {
            const vals = [
                g.promotionTypeName,
                v.promotionName,
                v.value,
                v.countUsed,
                v.sumDiscount
            ]
            const r = ws.addRow(vals)
            r.eachCell((cell: any, col: any) => {
                cell.border = {
                    top: { style: 'thin' }, left: { style: 'thin' },
                    bottom: { style: 'thin' }, right: { style: 'thin' }
                }
                if (col === 4) {
                    cell.alignment = { horizontal: 'right', vertical: 'middle' }
                    cell.numFmt = '#,##0'
                } else if (col === 5) {
                    cell.alignment = { horizontal: 'right', vertical: 'middle' }
                    cell.numFmt = '#,##0.00'
                } else {
                    cell.alignment = { horizontal: 'left', vertical: 'middle' }
                }
            })
            row++
        })
        ws.mergeCells(start, 1, row - 1, 1)
    })

    // 9) Summary footer
    const totalCount = rpt.groups.reduce((s: any, g: any) => s + g.promotions.reduce((a: any, v: any) => a + v.countUsed, 0), 0)
    const totalDiscount = rpt.groups.reduce((s: any, g: any) => s + g.promotions.reduce((a: any, v: any) => a + v.sumDiscount, 0), 0)
    const footer = ['', '', '', totalCount, totalDiscount]
    const fr = ws.addRow(footer)
    fr.getCell(3).value = 'รวม'
    fr.eachCell((cell: any, col: any) => {
        cell.font = { bold: true }
        cell.border = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
        }
        if (col === 4) {
            cell.alignment = { horizontal: 'right', vertical: 'middle' }
            cell.numFmt = '#,##0'
        } else if (col === 5) {
            cell.alignment = { horizontal: 'right', vertical: 'middle' }
            cell.numFmt = '#,##0.00'
        } else {
            cell.alignment = { horizontal: 'left', vertical: 'middle' }
        }
    })



    // 10) Save
    forceTextOnDateCells(wb)
    const buf = await wb.xlsx.writeBuffer()
    saveAs(
        new Blob([buf]),
        `${rpt.t('REPORT.TABLE.PROMOTION_USED.TITLE')}_${moment().format('YYYYMMDD')}.xlsx`
    )
}
